import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface StaffMember {
  id: string;
  email: string;
  display_name: string;
  role: 'admin' | 'staff';
  created_at: string;
}

export default function StaffManagement() {
  const { user, isAdmin } = useAuth();
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formName, setFormName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Chưa đăng nhập');

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-list-staff`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Lỗi tải danh sách');
      setStaffList(result.staff || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi không xác định');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchStaff();
  }, [isAdmin, fetchStaff]);

  const handleRoleChange = async (staffId: string, newRole: 'admin' | 'staff') => {
    if (staffId === user?.id) {
      showToast('Không thể thay đổi vai trò của chính mình');
      return;
    }
    setSavingId(staffId);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', staffId);

      if (updateError) throw new Error(updateError.message);

      setStaffList((prev) =>
        prev.map((s) => (s.id === staffId ? { ...s, role: newRole } : s))
      );
      showToast('Cập nhật vai trò thành công');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Lỗi cập nhật');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    if (!formEmail || !formPassword || formPassword.length < 6) {
      setCreateError('Vui lòng nhập email và mật khẩu tối thiểu 6 ký tự');
      return;
    }

    setCreating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Chưa đăng nhập');

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-create-staff`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formEmail,
            password: formPassword,
            displayName: formName || formEmail.split('@')[0],
          }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Tạo tài khoản thất bại');

      setCreateSuccess('Tạo tài khoản thành công!');
      setFormEmail('');
      setFormPassword('');
      setFormName('');
      fetchStaff();
      setTimeout(() => setShowModal(false), 1200);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Lỗi không xác định');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
        <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <i className="ri-shield-flash-line text-4xl text-gray-300"></i>
        </span>
        <p className="text-sm font-medium">Bạn không có quyền quản lý nhân viên</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium animate-in slide-in-from-top-2">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-emerald-400"></i>
          </span>
          {toast}
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3">
        <button
          onClick={() => {
            setShowModal(true);
            setCreateError('');
            setCreateSuccess('');
            setFormEmail('');
            setFormPassword('');
            setFormName('');
          }}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#f59e0b] text-white rounded-xl text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-user-add-line text-sm"></i>
          </span>
          Tạo tài khoản nhân viên
        </button>
        <div className="ml-auto text-xs text-gray-400">
          {staffList.length} tài khoản trong hệ thống
        </div>
      </div>

      {/* Table / Cards */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin block mb-2"></i>
          <p className="text-sm">Đang tải danh sách...</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-2xl p-12 text-center text-red-500">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-error-warning-line text-4xl"></i>
          </span>
          <p className="text-sm font-medium">{error}</p>
        </div>
      ) : staffList.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-team-line text-4xl"></i>
          </span>
          <p className="text-sm font-medium">Chưa có tài khoản nhân viên nào</p>
          <p className="text-xs mt-1">Bấm "Tạo tài khoản nhân viên" để thêm</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Nhân viên', 'Email', 'Vai trò', 'Ngày tạo'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {staffList.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                          <span className="text-amber-700 font-bold text-sm">
                            {(s.display_name || s.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {s.display_name || s.email.split('@')[0]}
                          </p>
                          <p className="text-[10px] text-gray-400">ID: {s.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-600 whitespace-nowrap">{s.email}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <select
                          value={s.role}
                          disabled={s.id === user?.id || savingId === s.id}
                          onChange={(e) => handleRoleChange(s.id, e.target.value as 'admin' | 'staff')}
                          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#f59e0b] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="admin">Admin</option>
                          <option value="staff">Nhân viên</option>
                        </select>
                        {savingId === s.id && (
                          <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
                        )}
                        {s.id === user?.id && (
                          <span className="text-[10px] text-gray-400">(bạn)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-400 whitespace-nowrap">
                      {formatDate(s.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="block md:hidden divide-y divide-gray-50">
            {staffList.map((s) => (
              <div key={s.id} className="px-4 py-3.5">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <span className="text-amber-700 font-bold text-sm">
                      {(s.display_name || s.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {s.display_name || s.email.split('@')[0]}
                      </p>
                      {s.id === user?.id && (
                        <span className="text-[10px] text-gray-400">(bạn)</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{s.email}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(s.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-12">
                  <select
                    value={s.role}
                    disabled={s.id === user?.id || savingId === s.id}
                    onChange={(e) => handleRoleChange(s.id, e.target.value as 'admin' | 'staff')}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#f59e0b] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="admin">Admin</option>
                    <option value="staff">Nhân viên</option>
                  </select>
                  {savingId === s.id && (
                    <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Staff Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                  <i className="ri-user-add-line text-amber-600 text-base"></i>
                </span>
                <h2 className="text-base font-bold text-gray-800">Tạo tài khoản nhân viên</h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreate} className="px-5 py-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Tên hiển thị</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-user-smile-line text-base"></i>
                  </span>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Tên nhân viên"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Email</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    placeholder="nhanvien@email.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Mật khẩu</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-lock-line text-base"></i>
                  </span>
                  <input
                    type="password"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    placeholder="Tối thiểu 6 ký tự"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                </div>
              </div>

              {createError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <span className="w-4 h-4 flex items-center justify-center text-red-500 shrink-0">
                    <i className="ri-error-warning-line text-sm"></i>
                  </span>
                  <p className="text-red-600 text-xs">{createError}</p>
                </div>
              )}
              {createSuccess && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                  <span className="w-4 h-4 flex items-center justify-center text-emerald-500 shrink-0">
                    <i className="ri-checkbox-circle-line text-sm"></i>
                  </span>
                  <p className="text-emerald-600 text-xs">{createSuccess}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={creating}
                className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {creating ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Đang tạo...
                  </span>
                ) : (
                  'Tạo tài khoản'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}